import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import type { YoastSEOData } from '@/lib/wordpress-seo';
import { BASE_URL, homeMeta, homeMetaEn } from '@/seo/meta';

interface SeoProps {
  title?: string;
  description?: string;
  url?: string;
  image?: string;
  type?: string;
  noindex?: boolean;
  hreflang?: Array<{ href: string; hreflang: string }>;
  yoast?: Partial<YoastSEOData> | null;
}

export default function Seo({
  title,
  description,
  image,
  type = 'website',
  noindex = false,
  hreflang = [],
  yoast = null,
}: SeoProps) {
  const location = useLocation();
  const SITE_URL = BASE_URL.replace(/\/$/, '');
  const canonical = yoast?.canonical ?? `${SITE_URL}${location.pathname}`;

  useEffect(() => {
    if (import.meta.env.DEV && canonical === SITE_URL) {
      console.warn('SEO ERROR: Canonical resolved to homepage');
    }
  }, [canonical, SITE_URL]);

  const isEnglishRoute = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const defaultMeta = isEnglishRoute ? homeMetaEn : homeMeta;

  const resolvedTitle = yoast?.title?.trim() || title?.trim() || defaultMeta.title;
  const resolvedDescription = yoast?.metaDesc?.trim() || description?.trim() || defaultMeta.description;

  const rawImage = image || defaultMeta.image || '/og-image.jpg';

  const fullImageUrl = (() => {
    if (!rawImage) {
      return `${SITE_URL}/og-image.jpg`;
    }
    if (/^https?:\/\//i.test(rawImage)) {
      return rawImage;
    }
    if (rawImage.startsWith('//')) {
      return `https:${rawImage}`;
    }
    return rawImage.startsWith('/')
      ? `${SITE_URL}${rawImage}`
      : `${SITE_URL}/${rawImage}`;
  })();

  const twitterTitle = resolvedTitle;
  const twitterDescription = resolvedDescription;
  const twitterCard = 'summary_large_image';

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonical} />
      {hreflang.map((link) => (
        <link key={`${link.hreflang}-${link.href}`} rel="alternate" href={link.href} hrefLang={link.hreflang} />
      ))}
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="PixelPro Solutions" />

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      <meta name="twitter:image" content={fullImageUrl} />

      {/* Additional meta tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
    </Helmet>
  );
}
